import { Component, Input, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeUrl } from '@angular/platform-browser';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { catchError, debounceTime, filter, take } from 'rxjs/operators';
import { MobileService } from 'src/services/mobile.service';
import { PromotionalContent } from '../../../../vendors/directus/interfaces/promotional-content.interface';
import { Router } from '@angular/router';
import { UtilityService } from '@modules/utility/services';
import { Order } from '../../../../interfaces/order.interface';
import { LocationPromotionalContent } from '../../../../interfaces/location.interface';

@Component({
  selector: 'app-promotional-content',
  templateUrl: './promotional-content.component.html',
  styleUrls: ['promotional-content.component.scss'],
})
export class PromotionalContentComponent implements OnInit, OnDestroy {
  @Select(state => state.app.promotionalContent) promotionalContent$: Observable<PromotionalContent>;
  @Select(state => state.order.order) order$: Observable<Order>;

  @Input() promoSection: 'cart' | 'upsell' | 'confirm' | 'category' | 'menu' | 'giftcard';

  promotionalImage: SafeUrl;
  promotionalImageMobile: SafeUrl;
  promotionalText: SafeHtml;

  isLoading = true; // Add loading state
  contentLoaded = false; // Track when content is fully loaded

  private subs: Subscription[] = [];

  constructor(
    public sanitizer: DomSanitizer,
    public mobileService: MobileService,
    private router: Router,
    private utilityService: UtilityService,
    private store: Store
  ) {}

  ngOnInit() {
    // Combine both streams to ensure we have all data before rendering
    this.subs.push(
      combineLatest([
        this.promotionalContent$.pipe(
          catchError(err => {
            console.error('Error getting promotional content:', err);
            return of(null);
          })
        ),
        this.order$.pipe(
          filter(order => !!order),
          catchError(err => {
            console.error('Error getting order:', err);
            return of(null);
          })
        ),
      ])
        .pipe(
          // Small debounce to ensure we have stable data
          debounceTime(50),
          // Only take the first emission to prevent multiple renders
          take(1)
        )
        .subscribe(([globalContent, order]) => {
          this.isLoading = true;

          // Clear existing values to prevent flashes of old content
          this.promotionalImage = null;
          this.promotionalImageMobile = null;
          this.promotionalText = null;

          // Process in priority order - first location content if available
          if (order?.location?.promotionalContent) {
            this.processLocationPromotionalContent(order.location.promotionalContent);
          }

          // Only if location content doesn't set values, use global content
          if (globalContent && (!this.promotionalImage || !this.promotionalImageMobile || !this.promotionalText)) {
            this.processGlobalContent(globalContent);
          }

          // Mark as loaded after all processing completes
          this.contentLoaded = true;
          this.isLoading = false;
        })
    );
  }

  processGlobalContent(pages: PromotionalContent) {
    try {
      switch (this.promoSection) {
        case 'cart':
          if (!this.promotionalImage && pages.cart_promotion_background?.data?.full_url) {
            this.promotionalImage = this.sanitizer.bypassSecurityTrustUrl(pages.cart_promotion_background.data.full_url);
          }
          if (!this.promotionalImageMobile && pages.cart_promotion_background_mobile?.data?.full_url) {
            this.promotionalImageMobile = this.sanitizer.bypassSecurityTrustUrl(pages.cart_promotion_background_mobile.data.full_url);
          }
          if (!this.promotionalText && pages.cart_promotion_text) {
            this.promotionalText = this.sanitizer.sanitize(SecurityContext.HTML, pages.cart_promotion_text);
          }
          break;

        case 'upsell':
          if (!this.promotionalImage && pages.upsell_promotion_background?.data?.full_url) {
            this.promotionalImage = this.sanitizer.bypassSecurityTrustUrl(pages.upsell_promotion_background.data.full_url);
          }
          if (!this.promotionalImageMobile && pages.upsell_promotion_background_mobile?.data?.full_url) {
            this.promotionalImageMobile = this.sanitizer.bypassSecurityTrustUrl(pages.upsell_promotion_background_mobile.data.full_url);
          }
          if (!this.promotionalText && pages.upsell_promotion_text) {
            this.promotionalText = this.sanitizer.sanitize(SecurityContext.HTML, pages.upsell_promotion_text);
          }
          break;

        case 'confirm':
          if (!this.promotionalImage && pages.confirm_promotion_background?.data?.full_url) {
            this.promotionalImage = this.sanitizer.bypassSecurityTrustUrl(pages.confirm_promotion_background.data.full_url);
          }
          if (!this.promotionalImageMobile && pages.confirm_promotion_background_mobile?.data?.full_url) {
            this.promotionalImageMobile = this.sanitizer.bypassSecurityTrustUrl(pages.confirm_promotion_background_mobile.data.full_url);
          }
          if (!this.promotionalText && pages.confirm_promotion_text) {
            this.promotionalText = this.sanitizer.sanitize(SecurityContext.HTML, pages.confirm_promotion_text);
          }
          break;

        case 'category':
          if (!this.promotionalImage && pages.category_card_background?.data?.full_url) {
            this.promotionalImage = this.sanitizer.bypassSecurityTrustUrl(pages.category_card_background.data.full_url);
          }
          if (!this.promotionalImageMobile && pages.category_card_background_mobile?.data?.full_url) {
            this.promotionalImageMobile = this.sanitizer.bypassSecurityTrustUrl(pages.category_card_background_mobile.data.full_url);
          }
          if (!this.promotionalText && pages.category_card_promotion) {
            this.promotionalText = this.sanitizer.sanitize(SecurityContext.HTML, pages.category_card_promotion);
          }
          break;

        case 'menu':
          if (!this.promotionalImage && pages.menu_promotion_background?.data?.full_url) {
            this.promotionalImage = this.sanitizer.bypassSecurityTrustUrl(pages.menu_promotion_background.data.full_url);
          }
          if (!this.promotionalImageMobile && pages.menu_promotion_background_mobile?.data?.full_url) {
            this.promotionalImageMobile = this.sanitizer.bypassSecurityTrustUrl(pages.menu_promotion_background_mobile.data.full_url);
          }
          if (!this.promotionalText && pages.menu_promotion_text) {
            this.promotionalText = this.sanitizer.sanitize(SecurityContext.HTML, pages.menu_promotion_text);
          }
          break;

        case 'giftcard':
          if (!this.promotionalImage && pages.giftcard_promotion_background?.data?.full_url) {
            this.promotionalImage = this.sanitizer.bypassSecurityTrustUrl(pages.giftcard_promotion_background.data.full_url);
          }
          if (!this.promotionalImageMobile && pages.giftcard_promotion_background_mobile?.data?.full_url) {
            this.promotionalImageMobile = this.sanitizer.bypassSecurityTrustUrl(pages.giftcard_promotion_background_mobile.data.full_url);
          }
          if (!this.promotionalText && pages.giftcard_promotion_text) {
            this.promotionalText = this.sanitizer.sanitize(SecurityContext.HTML, pages.giftcard_promotion_text);
          }
          break;

        default:
          if (!this.promotionalText) {
            this.promotionalText = this.sanitizer.bypassSecurityTrustHtml('');
          }
          break;
      }
    } catch (error) {
      console.error('Error processing global content:', error);
    }
  }

  processLocationPromotionalContent(locationContent: LocationPromotionalContent) {
    try {
      switch (this.promoSection) {
        case 'cart':
          if (locationContent.cartPromotionBackground?.data?.full_url) {
            this.promotionalImage = this.sanitizer.bypassSecurityTrustUrl(locationContent.cartPromotionBackground.data.full_url);
          }
          if (locationContent.cartPromotionBackgroundMobile?.data?.full_url) {
            this.promotionalImageMobile = this.sanitizer.bypassSecurityTrustUrl(
              locationContent.cartPromotionBackgroundMobile.data.full_url
            );
          }
          if (locationContent.cartPromotionText) {
            this.promotionalText = this.sanitizer.sanitize(SecurityContext.HTML, locationContent.cartPromotionText);
          }
          break;

        case 'upsell':
          if (locationContent.upsellPromotionBackground?.data?.full_url) {
            this.promotionalImage = this.sanitizer.bypassSecurityTrustUrl(locationContent.upsellPromotionBackground.data.full_url);
          }
          if (locationContent.upsellPromotionBackgroundMobile?.data?.full_url) {
            this.promotionalImageMobile = this.sanitizer.bypassSecurityTrustUrl(
              locationContent.upsellPromotionBackgroundMobile.data.full_url
            );
          }
          if (locationContent.upsellPromotionText) {
            this.promotionalText = this.sanitizer.sanitize(SecurityContext.HTML, locationContent.upsellPromotionText);
          }
          break;

        case 'confirm':
          if (locationContent.confirmPromotionBackground?.data?.full_url) {
            this.promotionalImage = this.sanitizer.bypassSecurityTrustUrl(locationContent.confirmPromotionBackground.data.full_url);
          }
          if (locationContent.confirmPromotionBackgroundMobile?.data?.full_url) {
            this.promotionalImageMobile = this.sanitizer.bypassSecurityTrustUrl(
              locationContent.confirmPromotionBackgroundMobile.data.full_url
            );
          }
          if (locationContent.confirmPromotionText) {
            this.promotionalText = this.sanitizer.sanitize(SecurityContext.HTML, locationContent.confirmPromotionText);
          }
          break;

        case 'menu':
          if (locationContent.menuPromotionBackground?.data?.full_url) {
            this.promotionalImage = this.sanitizer.bypassSecurityTrustUrl(locationContent.menuPromotionBackground.data.full_url);
          }
          if (locationContent.menuPromotionBackgroundMobile?.data?.full_url) {
            this.promotionalImageMobile = this.sanitizer.bypassSecurityTrustUrl(
              locationContent.menuPromotionBackgroundMobile.data.full_url
            );
          }
          if (locationContent.menuPromotionText) {
            this.promotionalText = this.sanitizer.sanitize(SecurityContext.HTML, locationContent.menuPromotionText);
          }
          break;
      }
    } catch (error) {
      console.error('Error processing location content:', error);
    }
  }

  ngOnDestroy() {
    if (this.subs) {
      this.subs.forEach((sub: Subscription) => sub.unsubscribe());
    }
  }

  onAnchorClick(event: Event) {
    let target = event.target as HTMLElement;
    while (target && target !== event.currentTarget) {
      if (target.tagName.toLowerCase() === 'a') {
        const href = target.getAttribute('href');

        if (href) {
          event.preventDefault();
          this.utilityService.handleDynamicLink(href);
        }
        break;
      }
      target = target.parentElement;
    }
  }
}
